import Typography from "@/design-system/Typography";
import Carousel from "@/design-system/carousel";
import PulseLoading from "@/design-system/pulse-loading";
import { FetchGet } from "@/lib/fetch-helper";
import { urlFormat } from "@Components/Modules/Helpers";
import OfferTile, { type PriceComparisonItemProps } from "@Components/comparison-offers-modal/offers-tile";
import { Brand } from "@Routes/BrandLinks";
import * as stylex from "@stylexjs/stylex";
import { useQuery } from "@tanstack/react-query";

import { ComposableSpacing, colors, genericStyles } from "../../styles/tokens.stylex";
import { languageSelector, useViewportInfoContext } from "../contexts";

const PCPopularBrandOffersResources = {
    title: "Polecane",
};

const PCPopularBrandOffersResourcesEn = {
    title: "Recommended",
};

interface PCPopularBrandOffersProps {
    brandId: string;
}

function OfferTileSkeleton() {
    return (
        <div {...stylex.props(styles.skeletonItem, styles.productContainerSkeleton)}>
            <PulseLoading styleX={styles.skeletonImage} />
            <div {...stylex.props(styles.skeletonContent)}>
                <PulseLoading
                    styleX={styles.skeletonTitle}
                    pulseNumber={2}
                    stylexItem={styles.skeletonItemRadius}
                />
                <PulseLoading
                    styleX={styles.skeletonPrice}
                    pulseNumber={2}
                    stylexItem={styles.skeletonItemRadius}
                />
            </div>
        </div>
    );
}

const SKELETON_ITEMS = Array.from({ length: 5 }, (_, i) => <OfferTileSkeleton key={i} />);

export function PCPopularBrandOffers({ brandId }: PCPopularBrandOffersProps) {
    const resources = languageSelector(PCPopularBrandOffersResources, PCPopularBrandOffersResourcesEn);

    const { isMobile } = useViewportInfoContext();

    const { data, isFetching, isFetched } = useQuery({
        queryKey: ["brand-popular-offers", brandId],
        queryFn: () =>
            FetchGet<PriceComparisonItemProps[]>(
                urlFormat(
                    Brand,
                    {
                        action: "PopularProductsList",
                        brandId: brandId,
                        size: 10,
                    },
                    true,
                ),
            ),
        placeholderData: (previousData) => previousData,
    });

    if (isFetched && (!data || data.length === 0)) {
        return null;
    }

    return (
        <section {...stylex.props(styles.container)}>
            <Typography textElement="h2" fontStyle="hS">
                {resources.title}
            </Typography>
            <br />
            <Carousel
                showArrows={true}
                styleX={styles.carouselContainer}
                arrowStyleX={styles.carouselArrow}
                carouselContainerStyleX={styles.divContainer}
                itemsToShow={isMobile ? 2.2 : 5}
                customGap={ComposableSpacing.s4}
                fullWidth
            >
                {isFetching && !data
                    ? SKELETON_ITEMS
                    : data?.map((offer) => (
                          <OfferTile
                              key={offer.id}
                              image={offer.image}
                              id={offer.id}
                              name={offer.name}
                              price={offer.price}
                              slug={offer.slug}
                          />
                      ))}
            </Carousel>
        </section>
    );
}

const styles = stylex.create({
    container: {
        marginBottom: ComposableSpacing.s8,
    },

    carouselContainer: {
        gap: ComposableSpacing.s4,
        scrollSnapType: "x mandatory",
    },

    productContainerSkeleton: {
        width: "100%",
    },

    divContainer: {
        padding: 0,
    },

    skeletonItem: {
        borderColor: "#e0e0e0",
        borderRadius: ComposableSpacing.s2,
        borderStyle: "solid",
        borderWidth: "1px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    skeletonImage: {
        aspectRatio: "1",
        width: "100%",
    },
    skeletonContent: {
        padding: ComposableSpacing.s3,
        gap: ComposableSpacing.s1,
        display: "flex",
        flexDirection: "column",
    },
    skeletonTitle: {
        gap: ComposableSpacing.s1,
        height: ComposableSpacing.s8,
        marginBottom: ComposableSpacing.s2,
    },
    skeletonPrice: {
        gap: ComposableSpacing.s1,
        height: ComposableSpacing.s7,
        width: "60%",
    },
    skeletonItemRadius: {
        borderRadius: ComposableSpacing.s2,
    },
    carouselArrow: {
        padding: ComposableSpacing.s2,
        borderRadius: genericStyles.borderRadiusFullRound,
        backgroundColor: colors.bg_Primary,
        opacity: 0.8,
        outlineColor: colors.b_Primary,
        outlineStyle: "solid",
        outlineWidth: "1px",
        height: "2.25rem",
        width: "2.25rem",
    },
});
